import React, { useEffect, useMemo, useState } from 'react';
import { LEGAL_CONSENT_NOTICE_ID, LegalConsentFields } from './LegalConsentFields';

/**
 * OnboardingForm — the single-page "finish setting up" form (password + name +
 * optional phone). Shared by the app-owned invite flow and the legacy
 * signed-in onboarding path. Built for the least tech-savvy homeowner: one
 * form, plain language, inline validation.
 *
 * `consent` (optional, invite flow only): { legal, notice, unavailable,
 * onRetry, retrying }. When omitted/null the form is exactly the legacy form.
 * When set, the agreement checkboxes render and submit stays disabled until
 * the required box is ticked; onSubmit then also receives
 * { agreedRequired, electronicNoticeGranted }.
 */

const CONSENT_HELPER_ID = 'onboarding-consent-helper';

export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_BYTES = 72; // Supabase Auth (bcrypt) limit

export const formatPhone = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const phoneDigits = (formatted) => formatted.replace(/\D/g, '');

const byteLength = (value) => {
  try {
    return new TextEncoder().encode(value).length;
  } catch (e) {
    return value.length;
  }
};

const FieldLabel = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2"
  >
    {children}
  </label>
);

const FieldError = ({ id, children }) => (
  <p id={id} className="text-red-600 text-xs font-medium mt-1.5" role="alert">
    {children}
  </p>
);

const inputClass =
  'w-full px-5 py-3.5 bg-section-bg border border-amber-100/40 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-2xl text-primary-dark placeholder-slate-400 text-base font-medium outline-none transition-all';

export const OnboardingForm = ({
  hoaName,
  email,
  propertyAddress,
  heroMessage = 'Your account has been reserved. Complete your profile below to access the portal.',
  submitLabel = 'Complete Setup',
  submittingLabel = 'Setting up your account…',
  submitting = false,
  submitError = '',
  onSubmit,
  consent = null,
}) => {
  const consentLegal = consent ? consent.legal || null : null;
  const [agreed, setAgreed] = useState(false);
  const [noticeGranted, setNoticeGranted] = useState(false);
  // A new legal block (e.g. after a 409 legal_version_changed) must be
  // reviewed and ticked again; every other field keeps its value.
  useEffect(() => {
    setAgreed(false);
    setNoticeGranted(false);
  }, [consentLegal]);
  const consentBlocksSubmit = Boolean(consent) && !(consentLegal && agreed);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});

  const errors = useMemo(() => {
    const errs = {};
    if (!firstName.trim()) errs.firstName = 'Please enter your first name';
    if (!lastName.trim()) errs.lastName = 'Please enter your last name';
    const enteredPhoneDigits = phoneDigits(phone);
    if (enteredPhoneDigits.length > 0 && enteredPhoneDigits.length !== 10) {
      errs.phone = 'Enter a complete 10-digit phone number or leave this field blank';
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      errs.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
    } else if (byteLength(password) > MAX_PASSWORD_BYTES) {
      errs.password = 'Password is too long — please use a shorter one';
    }
    if (confirmPassword !== password || confirmPassword.length === 0)
      errs.confirmPassword = "Passwords don't match";
    return errs;
  }, [firstName, lastName, phone, password, confirmPassword]);

  const isValid = Object.keys(errors).length === 0;
  const showError = (field) => touched[field] && errors[field];

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({
      firstName: true,
      lastName: true,
      phone: true,
      password: true,
      confirmPassword: true,
    });
    if (!isValid || submitting || consentBlocksSubmit) return;
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: formatPhone(phone),
      password,
      ...(consent ? { agreedRequired: agreed, electronicNoticeGranted: noticeGranted } : {}),
    });
  };

  const markTouched = (field) => () => setTouched((t) => ({ ...t, [field]: true }));

  return (
    <main className="min-h-screen bg-main-bg py-12 px-4 sm:px-6">
      <div className="max-w-xl mx-auto">
        {/* Brand header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-sidebar-bg flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-2xl" aria-hidden="true">
                landscape
              </span>
            </div>
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-primary-dark">
            {hoaName || 'your community'}
          </h1>
          <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mt-2">
            Welcome to Your Community Portal
          </p>
        </div>

        {/* Hero image — swap /hero-bg.jpg anytime */}
        <div
          className="h-[200px] rounded-2xl overflow-hidden warm-tactile-shadow mb-8 bg-cover bg-center relative"
          style={{ backgroundImage: "url('/hero-bg.jpg')" }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-5 left-6 right-6">
            <p className="text-white font-bold text-lg leading-snug">
              Welcome, {firstName.trim() || 'neighbor'}!
            </p>
            <p className="text-white/80 text-sm font-medium">{heroMessage}</p>
          </div>
        </div>

        {/* Form card */}
        <section className="bg-section-bg p-4 sm:p-8 rounded-[2.5rem]">
          <form
            onSubmit={handleSubmit}
            noValidate
            className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 p-6 sm:p-10 space-y-6"
          >
            <div className="rounded-2xl bg-section-bg border border-amber-100/40 px-5 py-4">
              <p className="text-sm font-bold text-primary-dark">Tell us who is signing in</p>
              <p className="text-xs font-medium text-slate-500 mt-1 leading-relaxed">
                Enter your own name, even if the property ownership record lists a household, trust,
                or company. We use this name for greetings, messages, and activity history.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <FieldLabel htmlFor="onboarding-first-name">First Name</FieldLabel>
                <input
                  id="onboarding-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={markTouched('firstName')}
                  className={inputClass}
                  placeholder="John"
                  autoComplete="given-name"
                  aria-invalid={Boolean(showError('firstName'))}
                  required
                />
                {showError('firstName') && <FieldError>{errors.firstName}</FieldError>}
              </div>
              <div>
                <FieldLabel htmlFor="onboarding-last-name">Last Name</FieldLabel>
                <input
                  id="onboarding-last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={markTouched('lastName')}
                  className={inputClass}
                  placeholder="Smith"
                  autoComplete="family-name"
                  aria-invalid={Boolean(showError('lastName'))}
                  required
                />
                {showError('lastName') && <FieldError>{errors.lastName}</FieldError>}
              </div>
            </div>

            <div>
              <FieldLabel htmlFor="onboarding-phone">Phone (optional)</FieldLabel>
              <input
                id="onboarding-phone"
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                onBlur={markTouched('phone')}
                className={inputClass}
                placeholder="(406) 555-1234"
                autoComplete="tel-national"
              />
              {showError('phone') && <FieldError>{errors.phone}</FieldError>}
            </div>

            <div>
              <FieldLabel htmlFor="onboarding-password">Password</FieldLabel>
              <div className="relative">
                <input
                  id="onboarding-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={markTouched('password')}
                  className={`${inputClass} pr-14`}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  aria-invalid={Boolean(showError('password'))}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-primary-dark transition-colors rounded-full"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              {showError('password') && <FieldError>{errors.password}</FieldError>}
            </div>

            <div>
              <FieldLabel htmlFor="onboarding-confirm-password">Confirm Password</FieldLabel>
              <input
                id="onboarding-confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={markTouched('confirmPassword')}
                className={inputClass}
                placeholder="Type your password again"
                autoComplete="new-password"
                aria-invalid={Boolean(showError('confirmPassword'))}
              />
              {showError('confirmPassword') && <FieldError>{errors.confirmPassword}</FieldError>}
            </div>

            {consent && (
              <LegalConsentFields
                legal={consentLegal}
                agreed={agreed}
                onAgreedChange={setAgreed}
                noticeGranted={noticeGranted}
                onNoticeChange={setNoticeGranted}
                notice={consent.notice || ''}
                unavailable={Boolean(consent.unavailable)}
                onRetry={consent.onRetry}
                retrying={Boolean(consent.retrying)}
              />
            )}

            {submitError && (
              <div
                className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-medium"
                role="alert"
              >
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || consentBlocksSubmit}
              aria-describedby={
                consentBlocksSubmit
                  ? consentLegal
                    ? CONSENT_HELPER_ID
                    : consent.notice
                      ? LEGAL_CONSENT_NOTICE_ID
                      : undefined
                  : undefined
              }
              className="w-full bg-secondary text-white px-8 py-4 rounded-full font-bold text-base hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {submitting ? submittingLabel : submitLabel}
            </button>
            {consentBlocksSubmit && consentLegal && (
              <p
                id={CONSENT_HELPER_ID}
                className="text-center text-xs font-medium text-slate-500"
              >
                Tick the agreement box to continue
              </p>
            )}
          </form>

          {/* Read-only context */}
          <div className="mt-6 space-y-2 text-center">
            {email && (
              <p className="text-sm font-medium text-slate-500">
                Your email: <span className="font-bold text-primary-dark">{email}</span>
              </p>
            )}
            {propertyAddress && (
              <p className="text-sm font-medium text-slate-500">
                Your property:{' '}
                <span className="font-bold text-primary-dark">{propertyAddress}</span>
              </p>
            )}
          </div>
        </section>

        <p className="text-center text-xs font-medium text-slate-400 mt-8">
          {hoaName || 'your community'}
        </p>
      </div>
    </main>
  );
};

export default OnboardingForm;
